"use client";

import { useCallback, useEffect, useMemo } from "react";
import debounce from "lodash.debounce";
import { RichTextEditor } from "@/features/lomdaEditorV2/components/scene/richTextEditor/richTextEditor";
import type { LomdaScene } from "@/features/lomdaEditorV2/lib/lomdaTypes";
import type { TextElement } from "@/features/lomdaEditorV2/lib/elements/types";
import {
    useDocumentActions,
    useEditorMode,
    usePendingCaret,
    useUiActions,
} from "@/features/lomdaEditorV2/store";
import { cn } from "@/lib/utils";

export function TextElementView({
    scene,
    element,
    isEditing,
}: {
    scene: LomdaScene;
    element: TextElement;
    isEditing: boolean;
}) {
    const { setTextElementHtml } = useDocumentActions();
    const { setActiveTextEditorId, selectElement, clearPendingCaret } =
        useUiActions();
    const pendingCaret = usePendingCaret();
    const mode = useEditorMode();

    const commit = useMemo(
        () =>
            debounce(
                (html: string) =>
                    setTextElementHtml(scene.id, element.id, html),
                400
            ),
        [setTextElementHtml, scene.id, element.id]
    );
    useEffect(() => () => commit.flush(), [commit]);
    // Flush the final HTML the moment editing ends so the history hook records
    // it in the single coalesced step for this edit session.
    useEffect(() => {
        if (!isEditing) commit.flush();
    }, [isEditing, commit]);

    const onFocusChange = useCallback(
        (focused: boolean) => {
            if (focused) selectElement(element.id);
            else setActiveTextEditorId(null);
        },
        [element.id, selectElement, setActiveTextEditorId]
    );

    // Vertical placement inside the box. "top" lets the editor fill the box
    // (so the whole area is clickable while editing); center/bottom shrink the
    // editor to its content and push it within the column.
    //
    // We use AUTO MARGINS rather than justify-content: when the text overflows
    // the box the margins collapse to 0, so the text stays anchored to the TOP
    // and spills off the bottom — the first lines are never clipped. When it
    // fits, the margins still center / bottom-align it.
    const va = element.verticalAlign ?? "top";
    const top = va === "top";
    const marginClass =
        va === "center" ? "my-auto" : va === "bottom" ? "mt-auto" : "";

    return (
        <div
            // Typography presets (typography.css) are scoped under
            // [data-lomda-scene]; carry it on the text wrapper so headings/lists
            // render styled. The scene root must NOT have it (that rule adds
            // padding that would offset the absolutely-positioned elements).
            data-lomda-scene={scene.template ?? "A"}
            // Clip text spill in render/preview (WYSIWYG), but let it stay
            // visible while editing so a custom block's hover controls, which
            // sit just outside the block edge, aren't cropped by the box.
            className={cn(
                "flex h-full w-full flex-col",
                mode === "render" ? "overflow-hidden" : "overflow-visible"
            )}
        >
            <RichTextEditor
                content={element.html}
                onUpdate={commit}
                editable={isEditing}
                autoFocus={isEditing}
                focusCaret={isEditing ? pendingCaret : null}
                onCaretConsumed={clearPendingCaret}
                onFocusChange={onFocusChange}
                className={cn(top ? "flex-1" : marginClass)}
                minHeight={top ? "100%" : "1.5em"}
            />
        </div>
    );
}
